import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, BellRing, AlertTriangle, Clock, Wallet, FileWarning, Banknote, RefreshCw, ArrowRight } from "lucide-react";

const KIND = {
  refill_request: { label: "Refill requests", icon: RefreshCw },
  approval_pending: { label: "Discount approvals", icon: Wallet },
  adjustment_proposed: { label: "Adjustments to confirm", icon: Wallet },
  overdue_invoice: { label: "Overdue invoices", icon: FileWarning },
  topup_required: { label: "Deposit top-ups", icon: AlertTriangle },
  deposit_expiry: { label: "Deposit instruments expiring", icon: Clock },
  credit_limit: { label: "Credit limit", icon: AlertTriangle },
  payout_due: { label: "Payouts due", icon: Banknote },
  exit_settlement: { label: "Exit settlements", icon: Clock },
  payment_reminder: { label: "Payment reminders", icon: BellRing },
};
const SEV = { high: "destructive", medium: "secondary", low: "outline" };

export default function Alerts() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/alerts").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const items = data.items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div>
      <PageHeader
        title="Alerts Centre"
        subtitle="Everything that needs an owner's decision today - refill intimations, overdue credit, deposit top-ups and payouts falling due."
        actions={<Button variant="outline" onClick={load} data-testid="alerts-refresh-button"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Open alerts" value={data.total} icon={BellRing} />
        <StatCard label="High priority" value={data.high} icon={AlertTriangle} accent />
        <StatCard label="Refill requests" value={data.counts.refill_request || 0} icon={RefreshCw} />
        <StatCard label="Overdue invoices" value={(data.counts.overdue_invoice || 0) + (data.counts.adjustment_proposed || 0)} icon={FileWarning} />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="alerts-filter-all">All ({data.total})</Button>
        {Object.entries(data.counts).map(([k, n]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`alerts-filter-${k}`}>{KIND[k]?.label || k} ({n})</Button>
        ))}
      </div>
      {items.length === 0 ? <Empty>Nothing needs attention. Enjoy the quiet.</Empty> : (
        <div className="space-y-2" data-testid="alerts-list">
          {items.map((it, i) => {
            const Icon = KIND[it.kind]?.icon || BellRing;
            return (
              <Card key={i} className={`p-4 flex items-start gap-4 ${it.severity === "high" ? "border-destructive/40" : ""}`} data-testid={`alert-item-${it.kind}`}>
                <span className={`mt-0.5 ${it.severity === "high" ? "text-destructive" : "text-primary"}`}><Icon className="w-5 h-5" strokeWidth={1.6} /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-sm">{it.title}</p>
                    <Badge variant={SEV[it.severity]} className="capitalize">{it.severity}</Badge>
                    {it.agent && <span className="text-xs text-muted-foreground">· {it.agent}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{it.detail}</p>
                </div>
                <Link to={it.link} className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">Open <ArrowRight className="w-3 h-3" /></Link>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
